import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Users, Copy, Check, Gift, TrendingUp, Ticket, Share2, Loader2, MousePointerClick } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReferralTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  const fetchReferralInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/referral/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReferralInfo(); }, [fetchReferralInfo]);

  const copyLink = () => {
    if (!data?.referral_url) return;
    navigator.clipboard.writeText(data.referral_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const shareLink = () => {
    if (!data?.referral_url) return;
    if (navigator.share) {
      navigator.share({
        title: 'eSIM4You',
        text: t('referral.shareText') || 'Get 5% off your first eSIM purchase with my referral link!',
        url: data.referral_url
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const stats = data.stats || {};

  return (
    <div className="space-y-6" data-testid="referral-tab">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/5 border border-teal-500/20 rounded-2xl p-6 sm:p-8" data-testid="referral-hero">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-teal-500/20 rounded-xl flex items-center justify-center">
            <Gift className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">{t('referral.title') || 'Invite Friends, Earn Rewards'}</h2>
            <p className="text-slate-400 text-sm">{t('referral.subtitle') || 'Share your link and both earn discounts!'}</p>
          </div>
        </div>

        <div className="bg-black/30 rounded-xl p-4 mb-4">
          <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">{t('referral.yourLink') || 'Your Referral Link'}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-teal-400 text-sm font-mono truncate" data-testid="referral-link">
              {data.referral_url}
            </code>
            <button
              onClick={copyLink}
              className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 p-2.5 rounded-lg transition-all flex-shrink-0"
              data-testid="copy-referral-link"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <button
              onClick={shareLink}
              className="bg-teal-500 hover:bg-teal-400 text-black p-2.5 rounded-lg transition-all flex-shrink-0"
              data-testid="share-referral-link"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-2xl mb-1">1</div>
            <p className="text-slate-300 text-xs font-medium">{t('referral.step1') || 'Share your link'}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-2xl mb-1">2</div>
            <p className="text-slate-300 text-xs font-medium">{t('referral.step2') || 'Friend gets 10% off'}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-2xl mb-1">3</div>
            <p className="text-slate-300 text-xs font-medium">{t('referral.step3') || 'You earn €2 credit'}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="referral-stats">
        {[
          { label: t('referral.clicks') || 'Clicks', value: stats.total_clicks || 0, icon: MousePointerClick, color: 'text-purple-400' },
          { label: t('referral.invited') || 'Invited', value: stats.total_referrals, icon: Users, color: 'text-blue-400' },
          { label: t('referral.purchases') || 'Purchases', value: stats.completed_purchases, icon: TrendingUp, color: 'text-emerald-400' },
          { label: t('referral.activeCoupons') || 'Active Coupons', value: stats.active_coupons, icon: Ticket, color: 'text-teal-400' },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white/5 border border-white/10 rounded-xl p-4 text-center"
          >
            <stat.icon className={`w-5 h-5 ${stat.color} mx-auto mb-2`} />
            <p className="text-white text-xl font-bold">{stat.value}</p>
            <p className="text-slate-500 text-xs">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent Referrals */}
      {data.recent_referrals && data.recent_referrals.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">{t('referral.recentReferrals') || 'Recent Referrals'}</h3>
          <div className="space-y-3">
            {data.recent_referrals.map((ref, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{ref.referred_name || 'Friend'}</p>
                    <p className="text-slate-500 text-xs">
                      {new Date(ref.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  ref.status === 'completed'
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {ref.status === 'completed'
                    ? (t('referral.purchased') || 'Purchased')
                    : (t('referral.signedUp') || 'Signed up')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reward Coupons */}
      {data.rewards && data.rewards.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">{t('referral.yourRewards') || 'Your Reward Coupons'}</h3>
          <div className="space-y-3">
            {data.rewards.map((reward, i) => (
              <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <Ticket className="w-5 h-5 text-teal-400" />
                  <div>
                    <code className="text-teal-400 font-mono text-sm font-bold">{reward.coupon_code}</code>
                    <p className="text-slate-500 text-xs mt-0.5">{t('referral.rewardAmount', '€2 credit')} - {new Date(reward.created_at).toLocaleDateString('it-IT')}</p>
                  </div>
                </div>
                <button
                  onClick={() => copyCode(reward.coupon_code)}
                  className="text-slate-400 hover:text-teal-400 p-1.5 rounded-lg hover:bg-white/5 transition-all"
                >
                  {copiedCode === reward.coupon_code ? <Check className="w-4 h-4 text-teal-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
